import React, { useEffect, useState } from 'react';
import { ShoppingBagIcon, ChevronLeftIcon, PlusIcon, TagIcon, PackageIcon, ImageIcon, SearchIcon, FilterIcon, LayoutGridIcon, ListIcon, QrCodeIcon, LinkIcon, CopyIcon, TrashIcon, EditIcon, CheckIcon, XIcon, ArchiveIcon, EyeIcon, DollarSignIcon, BarChartIcon } from 'lucide-react';
import { Shop } from './Shops';
import { ShopDashboard } from '../components/ShopDashboard';
// Product type definition
interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  discountedPrice?: number;
  categoryId: string;
  imageUrl: string;
  stockQuantity: number;
  sku: string;
  isActive: boolean;
}
// Category type definition
interface Category {
  id: string;
  name: string;
  description: string;
  productCount: number;
}
export function ShopDetails() {
  // Get shop ID from URL parameter
  const getShopId = () => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get('id');
    }
    return null;
  };
  const isNewShop = () => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get('new') === 'true';
    }
    return false;
  };
  // Shop state
  const [shop, setShop] = useState<Shop | null>(null);
  const [isEditing, setIsEditing] = useState(isNewShop());
  const [activeTab, setActiveTab] = useState<'dashboard' | 'products' | 'categories' | 'settings'>('dashboard');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | 'all'>('all');
  // Products and categories state
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  // Form state for new shop
  const [shopForm, setShopForm] = useState({
    name: '',
    description: '',
    logo: '•',
    color: '#f8d7d7',
    channels: ['ecommerce']
  });
  // Mock data loading
  useEffect(() => {
    const shopId = getShopId();
    if (shopId && !isNewShop()) {
      // Mock shop data
      const mockShop: Shop = {
        id: shopId,
        name: 'Beach Shop',
        description: 'Beachwear and accessories for summer holidays',
        logo: '•',
        color: '#f8d7d7',
        isOpen: true,
        channels: ['pos', 'ecommerce'],
        productsCount: 48,
        revenue: {
          total: 12580,
          currency: 'THB',
          growth: 15.3
        },
        ordersCount: 124
      };
      setShop(mockShop);
      setShopForm({
        name: mockShop.name,
        description: mockShop.description,
        logo: mockShop.logo,
        color: mockShop.color,
        channels: [...mockShop.channels]
      });
      // Mock categories
      setCategories([{
        id: 'cat1',
        name: 'Swimwear',
        description: 'Swimsuits and beachwear',
        productCount: 12
      }, {
        id: 'cat2',
        name: 'Accessories',
        description: 'Beach accessories',
        productCount: 18
      }, {
        id: 'cat3',
        name: 'Footwear',
        description: 'Sandals and beach shoes',
        productCount: 8
      }, {
        id: 'cat4',
        name: 'Souvenirs',
        description: 'Memorable items from the beach',
        productCount: 10
      }]);
      // Mock products
      setProducts([{
        id: 'prod1',
        name: 'Beach Towel',
        description: 'Large absorbent beach towel',
        price: 29.99,
        categoryId: 'cat2',
        imageUrl: 'https://images.unsplash.com/photo-1601924582970-9238bcb495d9?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80',
        stockQuantity: 45,
        sku: 'BT-001',
        isActive: true
      }, {
        id: 'prod2',
        name: 'Sunglasses',
        description: 'UV protection sunglasses',
        price: 49.99,
        discountedPrice: 39.99,
        categoryId: 'cat2',
        imageUrl: 'https://images.unsplash.com/photo-1577803645773-f96470509666?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80',
        stockQuantity: 32,
        sku: 'SG-002',
        isActive: true
      }, {
        id: 'prod3',
        name: 'Swim Shorts',
        description: 'Quick-dry swim shorts for men',
        price: 34.99,
        categoryId: 'cat1',
        imageUrl: 'https://images.unsplash.com/photo-1565128939522-7a321311972a?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80',
        stockQuantity: 28,
        sku: 'SS-003',
        isActive: true
      }, {
        id: 'prod4',
        name: 'Beach Sandals',
        description: 'Comfortable sandals for the beach',
        price: 24.99,
        categoryId: 'cat3',
        imageUrl: 'https://images.unsplash.com/photo-1603487742131-4160ec999306?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80',
        stockQuantity: 50,
        sku: 'BS-004',
        isActive: true
      }, {
        id: 'prod5',
        name: 'Beach Ball',
        description: 'Colorful inflatable beach ball',
        price: 12.99,
        categoryId: 'cat2',
        imageUrl: 'https://images.unsplash.com/photo-1610027240617-46262bfb32a8?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80',
        stockQuantity: 65,
        sku: 'BB-005',
        isActive: true
      }, {
        id: 'prod6',
        name: 'Beach Umbrella',
        description: 'Sun protection beach umbrella',
        price: 39.99,
        categoryId: 'cat2',
        imageUrl: 'https://images.unsplash.com/photo-1519046308686-24180d0f8087?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80',
        stockQuantity: 20,
        sku: 'BU-006',
        isActive: true
      }]);
    }
  }, []);
  // Filter products based on search and category
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) || product.description.toLowerCase().includes(searchTerm.toLowerCase()) || product.sku.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategoryId === 'all' || product.categoryId === selectedCategoryId;
    return matchesSearch && matchesCategory;
  });
  // Save shop
  const saveShop = () => {
    if (isNewShop()) {
      // Logic to create new shop
      window.location.href = '/shops';
    } else {
      // Logic to update shop
      setIsEditing(false);
    }
  };
  // Navigate back to shops
  const navigateToShops = () => {
    window.location.href = '/shops';
  };
  // Toggle product active status
  const toggleProductStatus = (productId: string) => {
    setProducts(products.map(product => product.id === productId ? {
      ...product,
      isActive: !product.isActive
    } : product));
  };
  // Render shop form
  const renderShopForm = () => <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-10765">
      <h2 className="text-lg font-medium text-gray-900 mb-4" data-id="element-10766">
        {isNewShop() ? 'Create New Shop' : 'Edit Shop'}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-10767">
        <div data-id="element-10768">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10769">
            Shop Name *
          </label>
          <input type="text" value={shopForm.name} onChange={e => setShopForm({
          ...shopForm,
          name: e.target.value
        })} className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Enter shop name" required data-id="element-10770" />
        </div>
        <div data-id="element-10771">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10772">
            Shop Logo
          </label>
          <input type="text" value={shopForm.logo} onChange={e => setShopForm({
          ...shopForm,
          logo: e.target.value
        })} className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Logo character or emoji" maxLength={2} data-id="element-10773" />
          <p className="mt-1 text-xs text-gray-500" data-id="element-10774">
            Enter a single character or emoji to use as shop logo
          </p>
        </div>
        <div className="col-span-2" data-id="element-10775">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10776">
            Description
          </label>
          <textarea value={shopForm.description} onChange={e => setShopForm({
          ...shopForm,
          description: e.target.value
        })} rows={3} className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Describe your shop" data-id="element-10777" />
        </div>
        <div data-id="element-10778">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-10779">
            Logo Background Color
          </label>
          <div className="flex items-center" data-id="element-10780">
            <input type="color" value={shopForm.color} onChange={e => setShopForm({
            ...shopForm,
            color: e.target.value
          })} className="w-10 h-10 border-0 p-0" data-id="element-10781" />
            <input type="text" value={shopForm.color} onChange={e => setShopForm({
            ...shopForm,
            color: e.target.value
          })} className="block ml-2 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" data-id="element-10782" />
          </div>
        </div>
        <div data-id="element-10783">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-10784">
            Shop Channels
          </label>
          <div className="space-y-2" data-id="element-10785">
            <label className="flex items-center" data-id="element-10786">
              <input type="checkbox" checked={shopForm.channels.includes('ecommerce')} onChange={e => {
              const newChannels = e.target.checked ? [...shopForm.channels, 'ecommerce'] : shopForm.channels.filter(c => c !== 'ecommerce');
              setShopForm({
                ...shopForm,
                channels: newChannels as ('pos' | 'ecommerce')[]
              });
            }} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-10787" />
              <span className="ml-2 text-sm text-gray-700" data-id="element-10788">
                E-Commerce (Online Shop)
              </span>
            </label>
            <label className="flex items-center" data-id="element-10789">
              <input type="checkbox" checked={shopForm.channels.includes('pos')} onChange={e => {
              const newChannels = e.target.checked ? [...shopForm.channels, 'pos'] : shopForm.channels.filter(c => c !== 'pos');
              setShopForm({
                ...shopForm,
                channels: newChannels as ('pos' | 'ecommerce')[]
              });
            }} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-10790" />
              <span className="ml-2 text-sm text-gray-700" data-id="element-10791">
                Point of Sale (Physical Store)
              </span>
            </label>
          </div>
        </div>
      </div>
      <div className="mt-6 flex justify-end space-x-3" data-id="element-10792">
        <button onClick={navigateToShops} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-10793">
          Cancel
        </button>
        <button onClick={saveShop} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium" data-id="element-10794">
          {isNewShop() ? 'Create Shop' : 'Save Changes'}
        </button>
      </div>
    </div>;
  // Render shop header
  const renderShopHeader = () => <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-10795">
      <div className="flex items-start justify-between" data-id="element-10796">
        <div className="flex items-start space-x-4" data-id="element-10797">
          <div className="w-16 h-16 rounded-md flex items-center justify-center flex-shrink-0" style={{
          backgroundColor: shop?.color
        }} data-id="element-10798">
            <span className="text-2xl font-bold text-red-600" data-id="element-10799">
              {shop?.logo}
            </span>
          </div>
          <div data-id="element-10800">
            <h2 className="text-2xl font-bold text-gray-900" data-id="element-10801">{shop?.name}</h2>
            <p className="text-gray-500 mt-1" data-id="element-10802">{shop?.description}</p>
            <div className="flex items-center mt-2 space-x-3" data-id="element-10803">
              {/* Shop channels */}
              {shop?.channels.includes('pos') && <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-800" data-id="element-10804">
                  POS
                </span>}
              {shop?.channels.includes('ecommerce') && <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800" data-id="element-10805">
                  E-Commerce
                </span>}
              {/* Shop status */}
              <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${shop?.isOpen ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-10806">
                {shop?.isOpen ? 'Open' : 'Closed'}
              </span>
            </div>
          </div>
        </div>
        {/* Shop actions */}
        <div className="flex items-center space-x-2" data-id="element-10807">
          <button onClick={() => setIsEditing(true)} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md" title="Edit Shop" data-id="element-10808">
            <EditIcon size={18} data-id="element-10809" />
          </button>
          <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md" title="QR Code" data-id="element-10810">
            <QrCodeIcon size={18} data-id="element-10811" />
          </button>
          <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md" title="Shop Link" data-id="element-10812">
            <LinkIcon size={18} data-id="element-10813" />
          </button>
        </div>
      </div>
      {/* Shop statistics */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 border-t border-gray-200 pt-4" data-id="element-10814">
        <div className="flex items-center" data-id="element-10815">
          <div className="p-2 bg-blue-50 rounded-md mr-3" data-id="element-10816">
            <PackageIcon size={18} className="text-blue-600" data-id="element-10817" />
          </div>
          <div data-id="element-10818">
            <p className="text-sm text-gray-500" data-id="element-10819">Products</p>
            <p className="text-lg font-medium" data-id="element-10820">{shop?.productsCount}</p>
          </div>
        </div>
        <div className="flex items-center" data-id="element-10821">
          <div className="p-2 bg-green-50 rounded-md mr-3" data-id="element-10822">
            <DollarSignIcon size={18} className="text-green-600" data-id="element-10823" />
          </div>
          <div data-id="element-10824">
            <p className="text-sm text-gray-500" data-id="element-10825">Revenue</p>
            <div className="flex items-center" data-id="element-10826">
              <p className="text-lg font-medium" data-id="element-10827">
                THB {shop?.revenue.total.toLocaleString()}
              </p>
              <span className={`ml-2 text-xs ${shop?.revenue.growth && shop.revenue.growth >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-10828">
                {shop?.revenue.growth && shop.revenue.growth >= 0 ? '+' : ''}
                {shop?.revenue.growth}%
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center" data-id="element-10829">
          <div className="p-2 bg-purple-50 rounded-md mr-3" data-id="element-10830">
            <ShoppingBagIcon size={18} className="text-purple-600" data-id="element-10831" />
          </div>
          <div data-id="element-10832">
            <p className="text-sm text-gray-500" data-id="element-10833">Orders</p>
            <p className="text-lg font-medium" data-id="element-10834">{shop?.ordersCount}</p>
          </div>
        </div>
      </div>
    </div>;
  // Render tabs
  const renderTabs = () => <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-10835">
      <div className="flex border-b border-gray-200" data-id="element-10836">
        <button onClick={() => setActiveTab('dashboard')} className={`pb-4 px-1 mr-8 text-sm font-medium ${activeTab === 'dashboard' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-10837">
          Dashboard
        </button>
        <button onClick={() => setActiveTab('products')} className={`pb-4 px-1 mr-8 text-sm font-medium ${activeTab === 'products' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-10838">
          Products
        </button>
        <button onClick={() => setActiveTab('categories')} className={`pb-4 px-1 mr-8 text-sm font-medium ${activeTab === 'categories' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-10839">
          Categories
        </button>
        <button onClick={() => setActiveTab('settings')} className={`pb-4 px-1 text-sm font-medium ${activeTab === 'settings' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-10840">
          Shop Settings
        </button>
      </div>
    </div>;
  // Add a new renderDashboardTab function
  const renderDashboardTab = () => {
    if (!shop) return null;
    return <ShopDashboard shop={shop} data-id="element-10841" />;
  };
  // Render products tab
  const renderProductsTab = () => <div className="space-y-4" data-id="element-10842">
      {/* Products toolbar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-10843">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0" data-id="element-10844">
          <div className="flex-1 w-full md:w-auto" data-id="element-10845">
            <div className="relative" data-id="element-10846">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-10847">
                <SearchIcon size={16} className="text-gray-400" data-id="element-10848" />
              </div>
              <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Search products by name, description or SKU" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-10849" />
            </div>
          </div>
          <div className="flex space-x-2 w-full md:w-auto" data-id="element-10850">
            <div className="relative flex-grow md:flex-grow-0" data-id="element-10851">
              <select className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" value={selectedCategoryId} onChange={e => setSelectedCategoryId(e.target.value)} data-id="element-10852">
                <option value="all" data-id="element-10853">All Categories</option>
                {categories.map(category => <option key={category.id} value={category.id} data-id="element-10854">
                    {category.name}
                  </option>)}
              </select>
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-10855">
                <FilterIcon size={16} className="text-gray-400" data-id="element-10856" />
              </div>
            </div>
            <div className="flex space-x-1" data-id="element-10857">
              <button onClick={() => setViewMode('grid')} className={`p-2 rounded ${viewMode === 'grid' ? 'bg-gray-200' : 'hover:bg-gray-100'}`} data-id="element-10858">
                <LayoutGridIcon size={18} className="text-gray-700" data-id="element-10859" />
              </button>
              <button onClick={() => setViewMode('list')} className={`p-2 rounded ${viewMode === 'list' ? 'bg-gray-200' : 'hover:bg-gray-100'}`} data-id="element-10860">
                <ListIcon size={18} className="text-gray-700" data-id="element-10861" />
              </button>
            </div>
            <button className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium flex items-center" data-id="element-10862">
              <PlusIcon size={16} className="mr-1" data-id="element-10863" />
              Add Product
            </button>
          </div>
        </div>
      </div>
      {/* Products list */}
      {viewMode === 'grid' ? <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-10864">
          {filteredProducts.map(product => <div key={product.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-id="element-10865">
              <div className="aspect-w-1 aspect-h-1 w-full overflow-hidden bg-gray-200" data-id="element-10866">
                <img src={product.imageUrl} alt={product.name} className="w-full h-48 object-cover" data-id="element-10867" />
                {!product.isActive && <div className="absolute inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center" data-id="element-10868">
                    <span className="px-2 py-1 bg-red-600 text-white text-xs font-medium rounded" data-id="element-10869">
                      Inactive
                    </span>
                  </div>}
              </div>
              <div className="p-4" data-id="element-10870">
                <h3 className="text-sm font-medium text-gray-900 truncate" data-id="element-10871">
                  {product.name}
                </h3>
                <p className="text-xs text-gray-500 mt-1 truncate" data-id="element-10872">
                  {product.description}
                </p>
                <div className="mt-2 flex justify-between items-center" data-id="element-10873">
                  <div data-id="element-10874">
                    {product.discountedPrice ? <div className="flex items-center" data-id="element-10875">
                        <span className="text-sm font-medium text-gray-900" data-id="element-10876">
                          THB {product.discountedPrice.toFixed(2)}
                        </span>
                        <span className="ml-2 text-xs text-gray-500 line-through" data-id="element-10877">
                          THB {product.price.toFixed(2)}
                        </span>
                      </div> : <span className="text-sm font-medium text-gray-900" data-id="element-10878">
                        THB {product.price.toFixed(2)}
                      </span>}
                  </div>
                  <span className="text-xs text-gray-500" data-id="element-10879">
                    Stock: {product.stockQuantity}
                  </span>
                </div>
                <div className="mt-3 flex justify-between items-center" data-id="element-10880">
                  <span className="text-xs text-gray-500" data-id="element-10881">
                    SKU: {product.sku}
                  </span>
                  <div className="flex space-x-1" data-id="element-10882">
                    <button onClick={() => toggleProductStatus(product.id)} className="p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded" data-id="element-10883">
                      {product.isActive ? <EyeIcon size={16} data-id="element-10884" /> : <EyeIcon size={16} data-id="element-10885" />}
                    </button>
                    <button className="p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded" data-id="element-10886">
                      <EditIcon size={16} data-id="element-10887" />
                    </button>
                    <button className="p-1 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded" data-id="element-10888">
                      <TrashIcon size={16} data-id="element-10889" />
                    </button>
                  </div>
                </div>
              </div>
            </div>)}
          {/* Add product card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 border-dashed flex items-center justify-center h-64" data-id="element-10890">
            <button className="flex flex-col items-center text-gray-500 hover:text-blue-600 p-4" data-id="element-10891">
              <PlusIcon size={24} className="mb-2" data-id="element-10892" />
              <span className="text-sm font-medium" data-id="element-10893">Add Product</span>
            </button>
          </div>
        </div> : <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-id="element-10894">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-10895">
            <thead className="bg-gray-50" data-id="element-10896">
              <tr data-id="element-10897">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10898">
                  Product
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10899">
                  Category
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10900">
                  Price
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10901">
                  Stock
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10902">
                  SKU
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10903">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10904">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-10905">
              {filteredProducts.map(product => {
            const category = categories.find(c => c.id === product.categoryId);
            return <tr key={product.id} className="hover:bg-gray-50" data-id="element-10906">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-10907">
                      <div className="flex items-center" data-id="element-10908">
                        <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded bg-gray-100" data-id="element-10909">
                          <img src={product.imageUrl} alt={product.name} className="h-10 w-10 object-cover" data-id="element-10910" />
                        </div>
                        <div className="ml-4" data-id="element-10911">
                          <div className="text-sm font-medium text-gray-900" data-id="element-10912">
                            {product.name}
                          </div>
                          <div className="text-sm text-gray-500 truncate max-w-xs" data-id="element-10913">
                            {product.description}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-10914">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-10915">
                        {category?.name || 'Uncategorized'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-10916">
                      {product.discountedPrice ? <div data-id="element-10917">
                          <span className="text-sm font-medium text-gray-900" data-id="element-10918">
                            THB {product.discountedPrice.toFixed(2)}
                          </span>
                          <span className="ml-2 text-xs text-gray-500 line-through" data-id="element-10919">
                            THB {product.price.toFixed(2)}
                          </span>
                        </div> : <span className="text-sm font-medium text-gray-900" data-id="element-10920">
                          THB {product.price.toFixed(2)}
                        </span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-10921">
                      <span className={`text-sm ${product.stockQuantity < 10 ? 'text-red-600 font-medium' : 'text-gray-900'}`} data-id="element-10922">
                        {product.stockQuantity}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-10923">
                      {product.sku}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-10924">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${product.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-10925">
                        {product.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-10926">
                      <div className="flex justify-end space-x-2" data-id="element-10927">
                        <button onClick={() => toggleProductStatus(product.id)} className="text-gray-500 hover:text-blue-600" data-id="element-10928">
                          {product.isActive ? <EyeIcon size={16} data-id="element-10929" /> : <EyeIcon size={16} data-id="element-10930" />}
                        </button>
                        <button className="text-gray-500 hover:text-blue-600" data-id="element-10931">
                          <EditIcon size={16} data-id="element-10932" />
                        </button>
                        <button className="text-gray-500 hover:text-red-600" data-id="element-10933">
                          <TrashIcon size={16} data-id="element-10934" />
                        </button>
                      </div>
                    </td>
                  </tr>;
          })}
            </tbody>
          </table>
        </div>}
      {/* Empty state */}
      {filteredProducts.length === 0 && <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center" data-id="element-10935">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-10936">
            <PackageIcon size={24} className="text-blue-600" data-id="element-10937" />
          </div>
          <h3 className="text-lg font-medium text-gray-900" data-id="element-10938">
            No products found
          </h3>
          <p className="text-gray-500 mt-2 mb-6" data-id="element-10939">
            {searchTerm || selectedCategoryId !== 'all' ? 'Try adjusting your search or filter criteria' : 'Get started by adding your first product'}
          </p>
          <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md inline-flex items-center transition-colors" data-id="element-10940">
            <PlusIcon size={16} className="mr-2" data-id="element-10941" />
            Add Product
          </button>
        </div>}
    </div>;
  // Render categories tab
  const renderCategoriesTab = () => <div className="space-y-4" data-id="element-10942">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex justify-between items-center" data-id="element-10943">
        <h3 className="text-lg font-medium text-gray-900" data-id="element-10944">
          Product Categories
        </h3>
        <button className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium flex items-center" data-id="element-10945">
          <PlusIcon size={16} className="mr-1" data-id="element-10946" />
          Add Category
        </button>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-id="element-10947">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-10948">
          <thead className="bg-gray-50" data-id="element-10949">
            <tr data-id="element-10950">
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10951">
                Category
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10952">
                Description
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10953">
                Products
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10954">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-10955">
            {categories.map(category => <tr key={category.id} className="hover:bg-gray-50" data-id="element-10956">
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-10957">
                  <div className="flex items-center" data-id="element-10958">
                    <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-10959">
                      <TagIcon size={16} className="text-blue-600" data-id="element-10960" />
                    </div>
                    <div className="ml-4" data-id="element-10961">
                      <div className="text-sm font-medium text-gray-900" data-id="element-10962">
                        {category.name}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-10963">
                  <div className="text-sm text-gray-500" data-id="element-10964">
                    {category.description}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-10965">
                  <div className="text-sm text-gray-900" data-id="element-10966">
                    {category.productCount} products
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-10967">
                  <div className="flex justify-end space-x-2" data-id="element-10968">
                    <button className="text-gray-500 hover:text-blue-600" data-id="element-10969">
                      <EditIcon size={16} data-id="element-10970" />
                    </button>
                    <button className="text-gray-500 hover:text-red-600" data-id="element-10971">
                      <TrashIcon size={16} data-id="element-10972" />
                    </button>
                  </div>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
      {/* Empty state */}
      {categories.length === 0 && <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center" data-id="element-10973">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-10974">
            <TagIcon size={24} className="text-blue-600" data-id="element-10975" />
          </div>
          <h3 className="text-lg font-medium text-gray-900" data-id="element-10976">
            No categories found
          </h3>
          <p className="text-gray-500 mt-2 mb-6" data-id="element-10977">
            Organize your products by creating categories
          </p>
          <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md inline-flex items-center transition-colors" data-id="element-10978">
            <PlusIcon size={16} className="mr-2" data-id="element-10979" />
            Add Category
          </button>
        </div>}
    </div>;
  // Render settings tab
  const renderSettingsTab = () => <div className="space-y-4" data-id="element-10980">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-10981">
        <p className="text-center text-gray-600" data-id="element-10982">
          Shop-specific settings are available in the main Settings page under
          the "Shop Settings" tab.
        </p>
        <div className="flex justify-center mt-4" data-id="element-10983">
          <button onClick={() => window.location.href = '/settings?tab=shop-settings'} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium" data-id="element-10984">
            Go to Shop Settings
          </button>
        </div>
      </div>
    </div>;
  // Update the renderActiveTabContent function to include the dashboard tab
  const renderActiveTabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return renderDashboardTab();
      case 'products':
        return renderProductsTab();
      case 'categories':
        return renderCategoriesTab();
      case 'settings':
        return renderSettingsTab();
      default:
        return renderDashboardTab();
    }
  };
  return <div className="space-y-4" data-id="element-10985">
      {/* Back button and page title */}
      <div className="flex items-center" data-id="element-10986">
        <button onClick={navigateToShops} className="mr-4 p-2 rounded-md hover:bg-gray-100" data-id="element-10987">
          <ChevronLeftIcon size={20} className="text-gray-600" data-id="element-10988" />
        </button>
        <h1 className="text-2xl font-bold text-gray-800 flex items-center" data-id="element-10989">
          <ShoppingBagIcon size={24} className="mr-2 text-blue-600" data-id="element-10990" />
          {isNewShop() ? 'Create New Shop' : shop?.name || 'Shop Details'}
        </h1>
      </div>
      {/* Main content */}
      <div className="space-y-4" data-id="element-10991">
        {isEditing || isNewShop() ? renderShopForm() : <>
            {renderShopHeader()}
            {renderTabs()}
            {renderActiveTabContent()}
          </>}
      </div>
    </div>;
}